import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly envPrefix: string;

  constructor(private logger: LoggerService) {
    // Create environment-specific prefix for localStorage keys
    this.envPrefix = `schenkwerk_${environment.env}`;
    this.logger.log(`🔐 [TokenStorage] Using environment prefix: ${this.envPrefix}`);
  }

  /**
   * Get environment-specific key for localStorage
   */
  private getKey(key: string): string {
    return `${this.envPrefix}_${key}`;
  }

  /**
   * Store access token with environment-specific key
   */
  setAccessToken(token: string): void {
    const key = this.getKey('access_token');
    localStorage.setItem(key, token);
    this.logger.log(`🔐 [TokenStorage] Stored access token with key: ${key}`);
  }

  /**
   * Get access token from environment-specific key
   */
  getAccessToken(): string | null {
    const key = this.getKey('access_token');
    const token = localStorage.getItem(key);
    this.logger.log(`🔐 [TokenStorage] Retrieved token with key: ${key}, found: ${!!token}`);
    return token;
  }

  /**
   * Remove access token
   */
  removeAccessToken(): void {
    const key = this.getKey('access_token');
    localStorage.removeItem(key);
    this.logger.log(`🔐 [TokenStorage] Removed token with key: ${key}`);
  }

  /**
   * Store company data with environment-specific keys
   */
  setCompanyData(data: {
    publicCompanyId: string;
    companyName: string;
    companyStripeAccountId: string | null;
    companyIsStripeOnboardingSuccess: boolean;
    companyIsProfileCompleted?: boolean;
  }): void {
    localStorage.setItem(this.getKey('public_company_id'), data.publicCompanyId);
    localStorage.setItem(this.getKey('company_name'), data.companyName);
    this.setCompanyStripeAccountId(data.companyStripeAccountId);
    localStorage.setItem(
      this.getKey('company_is_stripe_onboarding_success'),
      data.companyIsStripeOnboardingSuccess.toString(),
    );
    if (data.companyIsProfileCompleted !== undefined) {
      localStorage.setItem(
        this.getKey('company_is_profile_completed'),
        data.companyIsProfileCompleted.toString(),
      );
    }

    this.logger.log(`🏢 [TokenStorage] Stored company data for environment: ${environment.env}`);
  }

  /**
   * Get public company ID
   */
  getPublicCompanyId(): string | null {
    const key = this.getKey('public_company_id');
    const value = localStorage.getItem(key);
    this.logger.log(`🏢 [TokenStorage] Retrieved company ID with key: ${key}, value: ${value}`);
    return value;
  }

  /**
   * Get company name
   */
  getCompanyName(): string | null {
    const key = this.getKey('company_name');
    return localStorage.getItem(key);
  }

  /**
   * Overwrite the cached Stripe account ID. Needed when the connected account is
   * replaced server-side (a country change recreates it), because the cached ID
   * would otherwise keep pointing at the deleted account.
   */
  setCompanyStripeAccountId(accountId: string | null | undefined): void {
    // localStorage turns null into the string "null", which then reads as an account id.
    if (accountId) {
      localStorage.setItem(this.getKey('company_stripe_account_id'), accountId);
    } else {
      localStorage.removeItem(this.getKey('company_stripe_account_id'));
    }
  }

  /**
   * Get stripe account ID
   */
  getCompanyStripeAccountId(): string | null {
    const key = this.getKey('company_stripe_account_id');
    const accountId = localStorage.getItem(key);
    // Logins before the fix above stored a missing account as "null".
    return accountId && accountId !== 'null' && accountId !== 'undefined' ? accountId : null;
  }

  /**
   * Get profile completion status
   */
  getCompanyIsProfileCompleted(): boolean {
    const key = this.getKey('company_is_profile_completed');
    const value = localStorage.getItem(key);
    return value === 'true';
  }

  /**
   * Partner area (agencies). Its own token, kept apart from the company session so an
   * agency can switch into a client's account and come back without logging in again.
   */
  setPartnerSession(token: string, name: string): void {
    localStorage.setItem(this.getKey('partner_access_token'), token);
    localStorage.setItem(this.getKey('partner_name'), name);
  }

  getPartnerAccessToken(): string | null {
    return localStorage.getItem(this.getKey('partner_access_token'));
  }

  setPartnerName(name: string): void {
    localStorage.setItem(this.getKey('partner_name'), name);
  }

  getPartnerName(): string | null {
    return localStorage.getItem(this.getKey('partner_name'));
  }

  clearPartner(): void {
    localStorage.removeItem(this.getKey('partner_access_token'));
    localStorage.removeItem(this.getKey('partner_name'));
  }

  /**
   * Set while an agency works in a client's account: the company session then holds a
   * token naming the agency, and is renewed through the partner session.
   */
  setActingPartnerName(name: string | null): void {
    if (name) {
      localStorage.setItem(this.getKey('acting_partner_name'), name);
    } else {
      localStorage.removeItem(this.getKey('acting_partner_name'));
    }
  }

  getActingPartnerName(): string | null {
    return localStorage.getItem(this.getKey('acting_partner_name'));
  }

  /**
   * Clear all environment-specific authentication data of the company session
   * (the partner session stays, see clearPartner)
   */
  clearAll(): void {
    const keys = [
      'access_token',
      'public_company_id',
      'company_name',
      'company_stripe_account_id',
      'company_is_stripe_onboarding_success',
      'company_is_profile_completed',
      'acting_partner_name',
    ];

    keys.forEach((key) => {
      const envKey = this.getKey(key);
      localStorage.removeItem(envKey);
    });

    this.logger.log(`🧹 [TokenStorage] Cleared all tokens for environment: ${environment.env}`);
  }

  /**
   * Debug method to show all stored tokens across environments
   */
  debugShowAllTokens(): void {
    const environments = ['dev', 'qa', 'prod'];
    this.logger.group(`🔍 [TokenStorage] Debug: All stored tokens`);

    environments.forEach((env) => {
      const prefix = `schenkwerk_${env}`;
      const token = localStorage.getItem(`${prefix}_access_token`);
      const companyName = localStorage.getItem(`${prefix}_company_name`);

      this.logger.log(`${env.toUpperCase()}:`, {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        companyName: companyName || 'none',
      });
    });

    this.logger.groupEnd();
  }
}
